import classnames from 'classnames'
import { MouseEvent, TouchEvent, useState } from 'react'
import { DeleteOutlined } from '@ant-design/icons'
import { Modal, Empty, Popconfirm } from 'antd'

import Box from '~/core/Box'
import Image from '~/core/Image'
import Button from '~/core/Button'
import logger from '~/lib/logger'
import { AttachmentsType } from '~/components/FileUploadList/types'
import SafeDownloadLink from '~/apps/consumer/components/SafeDownloadLink'
import { PreviewModalType } from '../types'
import attachmentStyles from '../styles.css'

import PreviewModalFooter from './components/PreviewModalFooter'

export default function PreviewModal(props: PreviewModalType) {
    const {
        modalCss,
        selectedFileState,
        modalControls,
        canAttachmentBeDeleted,
        onRemove,
        closeFileListModal,
        rotateImg,
        toggleAttachment,
        openInNew,
        zoomIn,
        zoomOut,
        onPan,
        leftButtonDisabled,
        rightButtonDisabled,
        typeOfModule,
        attachmentTitle,
    } = props

    const { selectedFile, scale, rotation, offsetX, offsetY } =
        selectedFileState

    const [mouseDown, setMouseDown] = useState(false)
    const [dragging, setDragging] = useState(false)
    const [fromDragging, setFromDragging] = useState(false)
    const [dragData, setDragData] = useState({
        x: offsetX,
        y: offsetY,
        dx: 0,
        dy: 0,
    })

    const referentialAttachmentsData =
        selectedFile?.referentialAttachments || []
    const removeAttachments = (
        referentialAttachments: Partial<AttachmentsType>[],
    ) => {
        const referentialIds: string[] = referentialAttachments?.map(
            item => item?.id,
        )

        const areWorkOrderAttachmentsMultiple = referentialIds.length > 1

        const isTypeOfModuleQc = typeOfModule == 'inventoryQc'

        return (
            <Popconfirm
                title={
                    areWorkOrderAttachmentsMultiple ? (
                        <>
                            <>
                                <p style={{ lineHeight: '180%' }}>
                                    <span>
                                        Are you sure you want to delete this
                                        drawing from all parts?
                                    </span>{' '}
                                    <br />
                                    <span>
                                        This drawing attached to the following
                                        parts:
                                    </span>
                                </p>
                                {referentialAttachments.map((item, ind) => (
                                    <p key={ind}>
                                        <strong>
                                            {item?.workOrderItemName}
                                        </strong>
                                    </p>
                                ))}
                            </>
                            <p>This action cannot be undone.</p>
                        </>
                    ) : (
                        <>
                            <p>
                                Are you sure you want to delete this attachment?
                            </p>
                            <p>This action cannot be undone.</p>
                        </>
                    )
                }
                onConfirm={() => {
                    if (onRemove) {
                        onRemove(referentialIds)
                            .then(() => closeFileListModal())
                            .catch((error: any) =>
                                logger.exception(
                                    'Remove attachment request failed',
                                    error,
                                ),
                            )
                    }
                }}
                okText="Delete"
                cancelText="Cancel"
                okType="danger"
            >
                <Button
                    type="danger"
                    ghost={isTypeOfModuleQc && areWorkOrderAttachmentsMultiple}
                    style={{ marginLeft: 5 }}
                    className={attachmentStyles.fileListButton}
                    name="delete"
                    placement="remove-attachment-pop-confirm"
                >
                    {isTypeOfModuleQc ? (
                        `Delete from ${
                            areWorkOrderAttachmentsMultiple
                                ? 'all parts'
                                : 'this part'
                        } `
                    ) : (
                        <DeleteOutlined />
                    )}
                </Button>
            </Popconfirm>
        )
    }
    const clearDragData = () => {
        setDragging(false)
        setMouseDown(false)
    }
    const panStart = (
        panX: number,
        panY: number,
        evt: MouseEvent<EventTarget> | TouchEvent<EventTarget>,
    ) => {
        const newDragData = {
            dx: offsetX,
            dy: offsetY,
            x: panX,
            y: panY,
        }

        setMouseDown(true)
        setDragData(newDragData)
        evt.stopPropagation()
        evt.nativeEvent.stopImmediatePropagation()
        evt.preventDefault()
    }
    const panEnd = () => {
        setFromDragging(dragging)
        clearDragData()
        onPan(offsetX, offsetY)
    }
    const onMouseDown = (evt: MouseEvent<EventTarget>) => {
        panStart(evt.pageX, evt.pageY, evt)
    }
    const onMouseUp = () => {
        panEnd()
    }
    const getNewMatrixData = (distanceX: number, distanceY: number) => {
        const deltaX = dragData.x - distanceX
        const deltaY = dragData.y - distanceY

        const newOffsetX = dragData.dx - deltaX
        const newOffsetY = dragData.dy - deltaY

        return { newOffsetX, newOffsetY }
    }
    const updateMousePosition = (pageX: number, pageY: number) => {
        if (!mouseDown) {
            return
        }

        const { newOffsetX, newOffsetY } = getNewMatrixData(pageX, pageY)

        setDragging(true)
        onPan(newOffsetX, newOffsetY)
    }
    const onMouseMove = (evt: MouseEvent<EventTarget>) => {
        updateMousePosition(evt.pageX, evt.pageY)
    }
    const onClick = () => {
        if (fromDragging) {
            return
        }
    }
    const onTouchStart = (evt: TouchEvent<EventTarget>) => {
        const { pageX, pageY } = evt.touches[0]

        panStart(pageX, pageY, evt)
    }
    const onTouchEnd = () => {
        onMouseUp()
    }
    const onTouchMove = (evt: TouchEvent<EventTarget>) => {
        updateMousePosition(evt.touches[0].pageX, evt.touches[0].pageY)
    }

    return (
        <Modal
            centered
            visible={Boolean(selectedFile)}
            onCancel={() => {
                closeFileListModal()
                setFromDragging(false)
                clearDragData()
            }}
            afterClose={() => closeFileListModal()}
            className={classnames(modalCss)}
            bodyStyle={{
                overflow: 'hidden',
                margin: '10px',
                height: selectedFile?.name
                    ? 'calc(80h - 150px)'
                    : 'calc(80vh - 105px)',
            }}
            title={
                <div style={{ fontWeight: 300 }}>
                    <div style={{ fontSize: '20px', margin: '5px 0' }}>
                        {attachmentTitle || selectedFile?.name}
                    </div>
                    <div>
                        {referentialAttachmentsData
                            .map(item => item?.workOrderItemName)
                            .toString()}
                    </div>
                </div>
            }
            footer={
                <PreviewModalFooter
                    referentialAttachments={referentialAttachmentsData}
                    selectedFileState={selectedFileState}
                    removeAttachments={removeAttachments}
                    modalControls={modalControls}
                    rotateImg={rotateImg}
                    toggleAttachment={toggleAttachment}
                    openInNew={openInNew}
                    zoomIn={zoomIn}
                    zoomOut={zoomOut}
                    canAttachmentBeDeleted={canAttachmentBeDeleted}
                    leftButtonDisabled={leftButtonDisabled}
                    rightButtonDisabled={rightButtonDisabled}
                    typeOfModule={typeOfModule}
                />
            }
        >
            {selectedFile &&
                (selectedFile?.attachment?.mimeType?.startsWith('image') ? (
                    <div
                        className={attachmentStyles.imageWrapper}
                        style={{
                            transform: `matrix(${scale}, 0, 0, ${scale}, ${offsetX}, ${offsetY})`,
                        }}
                        onMouseDown={onMouseDown}
                        onMouseUp={onMouseUp}
                        onMouseMove={onMouseMove}
                        onClick={onClick}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        <Image
                            width="100%"
                            height="100%"
                            src={selectedFile?.attachment?.downloadUrl}
                            style={{
                                transform: `rotate(${rotation}deg)`,
                                objectFit: 'scale-down',
                            }}
                        />
                    </div>
                ) : (
                    <Box
                        height="100%"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Empty
                            imageStyle={{
                                height: '200px',
                            }}
                            description={
                                <span>
                                    {selectedFile?.attachment?.fileName}
                                </span>
                            }
                        >
                            <SafeDownloadLink
                                name="downloadDocs"
                                placement="fileUploadList"
                                downloadUrl={
                                    selectedFile?.attachment?.downloadUrl
                                }
                                fileName={selectedFile?.attachment?.fileName}
                            >
                                Click to download
                            </SafeDownloadLink>
                        </Empty>
                    </Box>
                ))}
        </Modal>
    )
}
